package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Country
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_back
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_city_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_city_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_info
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_map_adjust
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_map_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_pin_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_postal_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_postal_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_province_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_province_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_street_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_loc_street_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_error_province
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_error_required
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_location_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_next
import org.jetbrains.compose.resources.stringResource

/** Canadian provinces/territories (English display names; localize later like category names). */
private val CA_PROVINCES = listOf(
    "British Columbia", "Alberta", "Saskatchewan", "Manitoba", "Ontario", "Quebec",
    "New Brunswick", "Nova Scotia", "Prince Edward Island", "Newfoundland and Labrador",
    "Yukon", "Northwest Territories", "Nunavut",
)

/** Step 2 — Business Location (Stitch `a1244fb4`). Address fields + province picker + map preview. */
@Composable
fun AddBusinessLocationScreen(
    draft: AddBusinessDraft,
    onAddressLine: (String) -> Unit,
    onCity: (String) -> Unit,
    onProvince: (String) -> Unit,
    onPostalCode: (String) -> Unit,
    onBack: () -> Unit,
    onNext: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Inline validation (UX-9): an invalid Next tap marks the missing fields instead of a silently
    // disabled button.
    var showErrors by remember { mutableStateOf(false) }
    WizardScaffold(
        stepIndex = 2,
        title = stringResource(Res.string.ab_location_title),
        onBack = onBack,
        modifier = modifier,
        topBarTrailing = {
            AppIcon(
                name = "location_on",
                tint = AppTheme.colors.onPrimary,
                contentDescription = stringResource(Res.string.ab_loc_pin_a11y),
            )
        },
        bottomBar = {
            BackNextBar(
                onBack = onBack,
                onNext = { if (draft.isLocationValid) onNext() else showErrors = true },
                nextEnabled = true,
            )
        },
    ) {
        WizardCard {
            LabeledField(
                label = stringResource(Res.string.ab_loc_street_label),
                value = draft.addressLine,
                onValueChange = onAddressLine,
                placeholder = stringResource(Res.string.ab_loc_street_placeholder),
                isError = showErrors && draft.addressLine.isBlank(),
                errorText = stringResource(Res.string.ab_error_required),
            )
            LabeledField(
                label = stringResource(Res.string.ab_loc_city_label),
                value = draft.city,
                onValueChange = onCity,
                placeholder = stringResource(Res.string.ab_loc_city_placeholder),
                isError = showErrors && draft.city.isBlank(),
                errorText = stringResource(Res.string.ab_error_required),
            )
            // Scope suggestions to Canada — the province picker (CA_PROVINCES) is Canadian-only, so a
            // US municipality would never match the chosen province anyway.
            val citySuggestions = Metros.suggestCities(draft.city, Country.CA)
            if (citySuggestions.isNotEmpty()) {
                Row(
                    modifier = Modifier.horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    citySuggestions.forEach { suggestion ->
                        AppChip(
                            label = suggestion,
                            leadingIcon = "location_on",
                            onClick = { onCity(suggestion) },
                        )
                    }
                }
            }
            ProvinceField(selected = draft.province, onSelect = onProvince)
            if (showErrors && draft.province.isBlank()) {
                Text(
                    text = stringResource(Res.string.ab_error_province),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.error,
                )
            }
            LabeledField(
                label = stringResource(Res.string.ab_loc_postal_label),
                value = draft.postalCode,
                onValueChange = onPostalCode,
                placeholder = stringResource(Res.string.ab_loc_postal_placeholder),
                keyboardType = KeyboardType.Ascii,
            )
        }

        InfoNote(stringResource(Res.string.ab_loc_info))

        MapPreview()
    }
}

@Composable
private fun ProvinceField(selected: String, onSelect: (String) -> Unit) {
    var showSheet by remember { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        Text(
            text = stringResource(Res.string.ab_loc_province_label),
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surfaceContainer, AppTheme.shapes.md)
                .clickable { showSheet = true }
                .padding(AppTheme.spacing.md),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = selected.ifBlank { stringResource(Res.string.ab_loc_province_placeholder) },
                style = AppTheme.typography.bodyMd,
                color = if (selected.isNotBlank()) AppTheme.colors.onSurface else AppTheme.colors.onSurfaceVariant,
            )
            AppIcon("expand_more", tint = AppTheme.colors.onSurfaceVariant)
        }
    }

    if (showSheet) {
        AppBottomSheet(
            onDismissRequest = { showSheet = false },
            title = stringResource(Res.string.ab_loc_province_label),
        ) {
            CA_PROVINCES.forEach { province ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSelect(province); showSheet = false }
                        .padding(vertical = AppTheme.spacing.md),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(text = province, style = AppTheme.typography.bodyLg, color = AppTheme.colors.onSurface)
                    if (province == selected) AppIcon("check", tint = AppTheme.colors.primary)
                }
            }
        }
    }
}

@Composable
private fun MapPreview() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.lg),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppIcon("location_on", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
                Text(text = stringResource(Res.string.ab_loc_map_title), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
            }
            Text(text = stringResource(Res.string.ab_loc_map_adjust), style = AppTheme.typography.labelMd, color = AppTheme.colors.primary)
        }
        // Map integration is Phase 2; a neutral placeholder keeps the layout faithful for now.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(16f / 9f)
                .background(AppTheme.colors.surfaceContainer, AppTheme.shapes.card),
            contentAlignment = Alignment.Center,
        ) {
            AppIcon("map", size = AppTheme.sizes.iconLg, tint = AppTheme.colors.onSurfaceVariant)
        }
    }
}

@Composable
internal fun BackNextBar(onBack: () -> Unit, onNext: () -> Unit, nextEnabled: Boolean) {
    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
        AppButton(
            text = stringResource(Res.string.ab_back),
            onClick = onBack,
            modifier = Modifier.weight(1f),
            variant = AppButtonVariant.Secondary,
            leadingIcon = "arrow_back",
        )
        AppButton(
            text = stringResource(Res.string.ab_next),
            onClick = onNext,
            modifier = Modifier.weight(1f),
            trailingIcon = "arrow_forward",
            enabled = nextEnabled,
        )
    }
}
